import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { OrderModal } from '../components/OrderModal';
import { ArrowRight } from 'lucide-react';
import { getContent } from '../utils/contentStore';
import { getSiteConfig } from '../utils/siteConfig';
import { useSEO } from '../utils/useSEO';
import { useLanguage } from '../utils/i18n';
export function CatalogPage() {
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const allProducts = getContent().products;
  const showPrices = getSiteConfig().catalog.showPrices;
  const {
    t,
    loc
  } = useLanguage();
  useSEO('catalog');
  // Only show active products
  const products = allProducts.filter(p => p.active !== false);
  // Build dynamic line map from parentLine field
  const PRODUCT_LINES: Record<string, string[]> = {};
  products.forEach(p => {
    if (p.parentLine) {
      if (!PRODUCT_LINES[p.parentLine]) PRODUCT_LINES[p.parentLine] = [];
      PRODUCT_LINES[p.parentLine].push(p.id);
    }
  });
  const ALL_CHILD_IDS = new Set(Object.values(PRODUCT_LINES).flat());
  // Group products by category, skipping children
  const productsByCategory = products.reduce((acc, product) => {
    // Skip child products — they render inside parent line cards
    if (ALL_CHILD_IDS.has(product.id)) return acc;
    // Skip lines with no active children
    if (PRODUCT_LINES[product.id] && PRODUCT_LINES[product.id].length === 0) return acc;
    const category = loc(product.category);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {} as Record<string, typeof products>);
  const categories = Object.keys(productsByCategory);
  return <div className="pt-20 bg-gray-50 min-h-screen" data-id="element-975">
      <Section data-id="element-976">
        <div className="text-center mb-16" data-id="element-977">
          <h1 className="font-heading text-4xl md:text-6xl font-bold text-gray-900 uppercase mb-4 leading-tight" data-id="element-978">
            {t('catalog_page_title_1')}{' '}
            <span className="text-red-600" data-id="element-979">{t('catalog_page_title_2')}</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-980">
            {t('catalog_page_desc')}
          </p>
        </div>

        {/* Products by Category */}
        <div className="space-y-20" data-id="element-981">
          {categories.map(category => {
          const categoryProducts = productsByCategory[category];
          return <div key={category} data-id="element-982">
                {/* Category Header */}
                <div className="mb-8" data-id="element-983">
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 uppercase" data-id="element-984">
                    {category}
                  </h2>
                  <div className="h-1 w-24 bg-red-600 mt-4" data-id="element-985" />
                </div>

                {/* Product Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-986">
                  {categoryProducts.map(product => {
                const childIds = PRODUCT_LINES[product.id];
                // ── Product Line Card ──
                if (childIds) {
                  const children = childIds.map(id => products.find(p => p.id === id)).filter(Boolean) as typeof products;
                  if (children.length === 0) return null;
                  const minPrice = Math.min(...children.map(c => c.price));
                  return <div key={product.id} className="col-span-full bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row" data-id="element-987">
                          {/* Image */}
                          <div className="w-full md:w-2/5 lg:w-1/3 aspect-[4/3] md:aspect-auto bg-gray-100 relative group overflow-hidden" data-id="element-988">
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors z-10" data-id="element-989" />
                            <img src={product.image} alt={loc(product.name)} className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-700" data-id="element-990" />
                          </div>

                          {/* Content */}
                          <div className="p-6 md:p-8 flex-1 flex flex-col" data-id="element-991">
                            <h3 className="font-heading text-3xl font-bold text-gray-900 uppercase mb-2" data-id="element-992">
                              {loc(product.name)}
                            </h3>
                            <p className="text-gray-500 text-sm mb-4" data-id="element-993">
                              {loc(product.subtitle)}
                            </p>
                            <p className="text-gray-600 mb-6 flex-1 line-clamp-3 md:line-clamp-none text-sm leading-relaxed" data-id="element-994">
                              {loc(product.description)}
                            </p>

                            {/* Variant Selector */}
                            <div className="mb-6 bg-gray-50 p-4 rounded-md border border-gray-100" data-id="element-995">
                              <div className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-996">
                                <div className="w-2 h-2 bg-red-600 rounded-full" data-id="element-997" />
                                {t('catalog_line_variants')}
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-998">
                                {children.map(child => {
                            const variantName = loc(child.name).replace(loc(product.name) + ' ', '');
                            return <Link key={child.id} to={`/catalog/${child.id}`} className="flex flex-col p-3 bg-white border border-gray-200 rounded hover:border-red-600 hover:shadow-md transition-all group/variant" data-id="element-999">
                                      <span className="font-bold text-gray-900 group-hover/variant:text-red-600 transition-colors" data-id="element-1000">
                                        {variantName}
                                      </span>
                                      {showPrices && <span className="text-sm text-gray-500 mt-1" data-id="element-1001">
                                          {child.price} с / {loc(child.unit)}
                                        </span>}
                                    </Link>;
                          })}
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-auto pt-6 border-t border-gray-100 gap-4" data-id="element-1002">
                              {showPrices && <div className="flex items-baseline gap-2" data-id="element-1003">
                                  <span className="text-sm text-gray-500 uppercase tracking-wide" data-id="element-1004">
                                    {t('catalog_line_from')}
                                  </span>
                                  <span className="text-3xl font-bold text-gray-900" data-id="element-1005">
                                    {minPrice} с
                                  </span>
                                </div>}
                              <Link to={`/catalog/${product.id}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 transition-colors uppercase tracking-wide text-sm w-full sm:w-auto" data-id="element-1006">
                                {t('catalog_line_general')}
                                <ArrowRight className="h-4 w-4" data-id="element-1007" />
                              </Link>
                            </div>
                          </div>
                        </div>;
                }
                // ── Regular Product Card ──
                return <Link key={product.id} to={`/catalog/${product.id}`} className="group bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col" data-id="element-1008">
                        {/* Product Image */}
                        <div className="aspect-[4/3] overflow-hidden bg-gray-100 relative" data-id="element-1009">
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10" data-id="element-1010" />
                          <img src={product.image} alt={loc(product.name)} className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-500" data-id="element-1011" />
                        </div>

                        {/* Product Info */}
                        <div className="p-6 flex-1 flex flex-col" data-id="element-1012">
                          <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors uppercase" data-id="element-1013">
                            {loc(product.name)}
                          </h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-1" data-id="element-1014">
                            {loc(product.subtitle)}
                          </p>

                          {showPrices && <div className="flex items-baseline gap-2 mb-4" data-id="element-1015">
                              <span className="text-3xl font-bold text-gray-900" data-id="element-1016">
                                {product.price} с
                              </span>
                              <span className="text-gray-500 text-sm" data-id="element-1017">
                                / {loc(product.unit)}
                              </span>
                            </div>}

                          <div className="flex items-center gap-2 text-red-600 font-medium text-sm group-hover:gap-3 transition-all uppercase tracking-wide" data-id="element-1018">
                            {t('catalog_view_product')}
                            <ArrowRight className="h-4 w-4" data-id="element-1019" />
                          </div>
                        </div>
                      </Link>;
              })}
                </div>
              </div>;
        })}
        </div>
      </Section>

      <OrderModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} productName={selectedProduct || ''} data-id="element-1020" />
    </div>;
}